<template>
  <div class="ir-list-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="rgba(255, 255, 255, 0.85)"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Modern Notification Snackbar -->
    <v-snackbar
      v-model="showsnackbar"
      :color="snackbarColor || '#1e293b'"
      right
      top
      :timeout="timeout"
      elevation="8"
      class="modern-snackbar"
    >
      <div class="d-flex align-center justify-space-between w-100 py-1">
        <div class="d-flex align-center gap-2">
          <v-icon color="#ffffff" size="18">
            {{ snackbarColor === '#ef4444' ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
          </v-icon>
          <span class="snackbar-text font-weight-medium white--text">{{ msg }}</span>
        </div>
        <v-btn icon small @click="showsnackbar = false" class="ml-2">
          <v-icon color="#ffffff" size="16">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Page Header: Breadcrumb, Title, Subtitle & Action Buttons -->
    <div class="page-header-wrap mb-4">
      <div class="d-flex flex-wrap align-center justify-space-between">
        <!-- Left: Breadcrumb + Title + Subtitle -->
        <div class="header-text-block">
          <PageBreadcrumb :items="[{ text: 'Purchase', to: '/PurchaseList' }, { text: 'Issue & Receipt' }]" class="mb-1" />
          <p class="page-subtitle mb-0">Manage and track all your gold, silver, diamond, and cash vouchers in one place.</p>
        </div>

        <!-- Right: Header Action Buttons -->
        <div class="header-actions-group d-flex flex-wrap align-center mt-3 mt-sm-0">
          <!-- + New Voucher (Amber button) -->
          <v-btn
            depressed
            class="btn-new-voucher mr-2"
            @click="$router.push('/IssueReceipt')"
          >
            <v-icon left size="16">mdi-plus</v-icon>
            New Voucher
          </v-btn>

          <!-- Summary button -->
          <v-btn
            outlined
            depressed
            class="btn-action-outline mr-2"
            @click="summaryDialog = true"
          >
            <v-icon left size="16" class="btn-icon">mdi-poll</v-icon>
            Summary
          </v-btn>

          <!-- Daily Report button -->
          <v-btn
            outlined
            depressed
            class="btn-action-outline mr-2"
            @click="$router.push('/DailyIssueReceiptReport')"
          >
            <v-icon left size="16" class="btn-icon">mdi-file-document-outline</v-icon>
            Daily Report
          </v-btn>

          <!-- More options menu button (Three dots) -->
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                depressed
                class="btn-action-outline btn-icon-only"
                v-bind="attrs"
                v-on="on"
                title="More Options"
              >
                <v-icon size="18" color="#64748b">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="rounded-lg shadow-sm">
              <v-list-item @click="getList()">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="#9a6b27">mdi-refresh</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-size-13">Refresh List</v-list-item-title>
              </v-list-item>
              <v-list-item @click="resetFilters()">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="#64748b">mdi-filter-remove-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-size-13">Clear Filters</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- Filter Bar Card (Clean, Spacious layout matching PurchaseList) -->
    <div class="filter-card-bar mb-4">
      <div class="d-flex flex-wrap align-center filter-row">
        <!-- From Date -->
        <div class="filter-item date-item">
          <v-menu
            ref="menuFrom"
            v-model="menuFrom"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="fromDate ? formatDate(fromDate) : ''"
                placeholder="From Date"
                background-color="#ffffff"
                outlined
                color="#9a6b27"
                readonly
                hide-details
                clearable
                class="filter-input-field"
                dense
                prepend-inner-icon="mdi-calendar-range-outline"
                v-bind="attrs"
                v-on="on"
                @click:clear="fromDate = ''"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              no-title
              scrollable
              color="#9a6b27"
              @change="$refs.menuFrom.save(fromDate)"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- To Date -->
        <div class="filter-item date-item">
          <v-menu
            ref="menuTo"
            v-model="menuTo"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="toDate ? formatDate(toDate) : ''"
                placeholder="To Date"
                background-color="#ffffff"
                outlined
                color="#9a6b27"
                readonly
                hide-details
                clearable
                class="filter-input-field"
                dense
                prepend-inner-icon="mdi-calendar-range-outline"
                v-bind="attrs"
                v-on="on"
                @click:clear="toDate = ''"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              no-title
              scrollable
              color="#9a6b27"
              @change="$refs.menuTo.save(toDate)"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- Type Select -->
        <div class="filter-item type-item">
          <v-select
            v-model="typeFilter"
            :items="typeOptions"
            item-text="text"
            item-value="value"
            placeholder="All Types"
            outlined
            hide-details
            dense
            background-color="#ffffff"
            class="filter-input-field type-select"
            color="#9a6b27"
          ></v-select>
        </div>

        <!-- Keyword Search (Searches Voucher No, Invoice No, and Supplier Name) -->
        <div class="filter-item search-item flex-grow-1">
          <v-text-field
            v-model="keyword"
            background-color="#ffffff"
            clearable
            hide-details
            color="#9a6b27"
            placeholder="Search voucher no., supplier name, invoice..."
            outlined
            class="filter-input-field"
            dense
            prepend-inner-icon="mdi-magnify"
            @keyup.enter="page = 1; getList()"
          ></v-text-field>
        </div>

        <!-- Search Button -->
        <v-btn
          depressed
          class="btn-search-amber"
          @click="page = 1; getList()"
        >
          <v-icon left size="16">mdi-magnify</v-icon>
          Search
        </v-btn>

        <!-- Reset Button -->
        <v-btn
          outlined
          depressed
          class="btn-reset-outline"
          @click="resetFilters()"
        >
          <v-icon left size="16" class="mr-1">mdi-refresh</v-icon>
          Reset
        </v-btn>
      </div>
    </div>

    <!-- Main Table Card -->
    <v-card class="table-card rounded-2xl" elevation="0">
      <div class="table-responsive">
        <table class="custom-table">
          <thead>
            <tr>
              <th class="col-sno text-left pl-4" @click="sortBy('sno')">
                <span>#</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-voucher text-left" @click="sortBy('voucherNo')">
                <span>VOUCHER NO</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-date text-left" @click="sortBy('date')">
                <span>DATE</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-type text-center" @click="sortBy('type')">
                <span>TYPE</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-supplier text-left" @click="sortBy('supplierName')">
                <span>SUPPLIER NAME</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-inv-no text-left" @click="sortBy('invoiceNumber')">
                <span>INVOICE NO</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-mode text-center" @click="sortBy('paymentMode')">
                <span>MODE</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-weight text-right" @click="sortBy('totalWeight')">
                <span>TOTAL WT (GM)</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-amount text-right" @click="sortBy('totalAmount')">
                <span>TOTAL AMOUNT (₹)</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-actions text-center pr-4">ACTION</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(value, i) in displayedList"
              :key="value._id || i"
              class="table-row"
            >
              <!-- S.NO -->
              <td class="col-sno text-left pl-4 cell-sno">
                {{ (page - 1) * itemsPerPage + i + 1 }}
              </td>

              <!-- Voucher No -->
              <td class="col-voucher text-left">
                <span class="voucher-pill">
                  {{ (value.prefix ? value.prefix + '-' : '') + (value.voucherNo || '-') }}
                </span>
              </td>

              <!-- Date -->
              <td class="col-date text-left">
                <span class="date-text">{{ formatDate(value.date) }}</span>
              </td>

              <!-- Type (Issue / Receipt Pill with colored dot) -->
              <td class="col-type text-center">
                <span
                  v-if="value.type === 'Issue'"
                  class="type-pill type-issue"
                >
                  <span class="type-dot dot-issue"></span>
                  Issue
                </span>
                <span
                  v-else-if="value.type === 'Receipt'"
                  class="type-pill type-receipt"
                >
                  <span class="type-dot dot-receipt"></span>
                  Receipt
                </span>
                <span v-else class="text-muted">{{ value.type || '-' }}</span>
              </td>

              <!-- Supplier Name -->
              <td class="col-supplier text-left">
                <span v-if="value.supplierId && value.supplierId.supplierName" class="supplier-text">
                  {{ value.supplierId.supplierName }}
                </span>
                <span v-else-if="value.supplierName" class="supplier-text">
                  {{ value.supplierName }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Invoice No -->
              <td class="col-inv-no text-left">
                <span v-if="value.invoiceNumber" class="inv-text">{{ value.invoiceNumber }}</span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Settlement Mode (Weight / Cash / Pay Instead of Weight) -->
              <td class="col-mode text-center">
                <span v-if="value.settlementMode" class="mode-pill">
                  <v-icon x-small :color="settlementModeMeta(value.settlementMode).color" class="mr-1">
                    {{ settlementModeMeta(value.settlementMode).icon }}
                  </v-icon>
                  {{ settlementModeMeta(value.settlementMode).label }}
                </span>
                <span v-else-if="value.paymentMode" class="mode-pill">
                  <v-icon x-small :color="value.paymentMode === 'Cash' ? '#16a34a' : '#2563eb'" class="mr-1">
                    {{ value.paymentMode === 'Cash' ? 'mdi-cash' : 'mdi-bank' }}
                  </v-icon>
                  {{ value.paymentMode }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Total Weight (gm) -->
              <td class="col-weight text-right">
                <span class="weight-val">
                  {{ (getVoucherTotalWeight(value)).toFixed(2) }}
                </span>
              </td>

              <!-- Total Amount (₹) -->
              <td class="col-amount text-right">
                <span class="amount-val">
                  ₹ {{ formatCurrency(getVoucherTotalAmount(value)) }}
                </span>
              </td>

              <!-- Action Buttons -->
              <td class="col-actions text-center pr-4">
                <div class="d-flex align-center justify-center">
                  <!-- Quick View Button -->
                  <button
                    type="button"
                    class="table-action-btn mr-2"
                    title="View Voucher Details"
                    @click="viewVoucher(value)"
                  >
                    <v-icon size="16" color="#64748b">mdi-eye-outline</v-icon>
                  </button>

                  <!-- More Options Menu -->
                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <button
                        type="button"
                        class="table-action-btn"
                        v-bind="attrs"
                        v-on="on"
                        title="More Actions"
                      >
                        <v-icon size="16" color="#64748b">mdi-dots-vertical</v-icon>
                      </button>
                    </template>
                    <v-list dense class="rounded-lg shadow-sm">
                      <v-list-item @click="viewVoucher(value)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="#9a6b27">mdi-eye-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-size-13">View Details</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="printVoucherDirect(value)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="#9a6b27">mdi-printer</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-size-13">Print Voucher</v-list-item-title>
                      </v-list-item>
                      <v-divider class="my-1"></v-divider>
                      <v-list-item @click="confirmDelete(value)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="#ef4444">mdi-delete-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-size-13 text-red">Delete Voucher</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="!displayedList || displayedList.length === 0">
              <td colspan="10" class="text-center py-12 text-muted">
                <div class="empty-state-wrap">
                  <div class="empty-icon-circle mb-3">
                    <v-icon size="36" color="#94a3b8">mdi-receipt-text-outline</v-icon>
                  </div>
                  <div class="empty-title">No vouchers found</div>
                  <div class="empty-subtitle">Try adjusting your date range, type, or search keywords.</div>
                  <v-btn
                    depressed
                    small
                    color="#9a6b27"
                    dark
                    class="mt-3 text-capitalize rounded-lg font-weight-bold px-4"
                    @click="$router.push('/IssueReceipt')"
                  >
                    <v-icon left x-small>mdi-plus</v-icon>
                    Create New Voucher
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Table Footer / Pagination -->
      <div class="table-footer d-flex flex-wrap align-center justify-space-between py-3 px-4">
        <!-- Records Counter Text -->
        <div class="footer-records-text">
          Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalRecordsCount }} records
        </div>

        <!-- Pagination Controls -->
        <div class="footer-pagination-wrap d-flex align-center">
          <button
            type="button"
            class="pagination-btn pagination-arrow mr-1"
            :disabled="page <= 1"
            @click="page > 1 ? page-- : null"
            title="Previous Page"
          >
            ‹
          </button>

          <button
            v-for="p in visiblePages"
            :key="p"
            type="button"
            class="pagination-btn pagination-page-num mr-1"
            :class="{ 'pagination-active': page === p }"
            @click="page = p"
          >
            {{ p }}
          </button>

          <button
            type="button"
            class="pagination-btn pagination-arrow mr-3"
            :disabled="page >= totalPages"
            @click="page < totalPages ? page++ : null"
            title="Next Page"
          >
            ›
          </button>

          <!-- Dropdown for Items Per Page -->
          <div class="page-size-select-wrap">
            <v-select
              v-model="itemsPerPage"
              :items="pageSizeOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              color="#9a6b27"
              class="page-size-select"
            ></v-select>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Summary Modal Dialog (Organized & Beautiful) -->
    <v-dialog v-model="summaryDialog" max-width="560px">
      <v-card class="rounded-2xl pa-5">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center">
            <div class="dialog-icon-badge mr-2">
              <v-icon color="#9a6b27" size="20">mdi-poll</v-icon>
            </div>
            <h3 class="font-weight-bold text-dark" style="font-size: 18px;">Issue &amp; Receipt Summary</h3>
          </div>
          <v-btn icon small @click="summaryDialog = false">
            <v-icon color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-4" style="border-color: #f1ece5;"></v-divider>

        <v-layout wrap class="mx-n2">
          <!-- Total Vouchers -->
          <v-flex xs6 class="pa-2">
            <div class="summary-metric-card">
              <span class="metric-label">Total Vouchers</span>
              <span class="metric-value">{{ totalRecordsCount }}</span>
              <span class="metric-sub">Active records</span>
            </div>
          </v-flex>

          <!-- Total Net Weight -->
          <v-flex xs6 class="pa-2">
            <div class="summary-metric-card">
              <span class="metric-label">Total Net Weight</span>
              <span class="metric-value">{{ totalWeightSum.toFixed(2) }} gm</span>
              <span class="metric-sub">Combined item weights</span>
            </div>
          </v-flex>

          <!-- Total Issues -->
          <v-flex xs6 class="pa-2">
            <div class="summary-metric-card status-issue-bg">
              <span class="metric-label text-blue">Issues Given</span>
              <span class="metric-value text-blue">{{ issueCount }}</span>
              <span class="metric-sub text-blue">{{ issueWeight.toFixed(2) }} gm issued</span>
            </div>
          </v-flex>

          <!-- Total Receipts -->
          <v-flex xs6 class="pa-2">
            <div class="summary-metric-card status-receipt-bg">
              <span class="metric-label text-green">Receipts Taken</span>
              <span class="metric-value text-green">{{ receiptCount }}</span>
              <span class="metric-sub text-green">{{ receiptWeight.toFixed(2) }} gm received</span>
            </div>
          </v-flex>

          <!-- Total Amount -->
          <v-flex xs12 class="pa-2">
            <div class="summary-metric-card total-amount-bg">
              <span class="metric-label">Total Settlement Amount</span>
              <span class="metric-value text-amber">₹ {{ formatCurrency(totalAmountSum) }}</span>
              <span class="metric-sub">Sum of cash / bank settlement amounts</span>
            </div>
          </v-flex>
        </v-layout>

        <div class="text-right mt-4">
          <v-btn
            color="#9a6b27"
            dark
            depressed
            class="rounded-lg text-capitalize px-5 font-weight-bold"
            @click="summaryDialog = false"
          >
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Voucher Detail Modal Dialog -->
    <v-dialog v-model="viewDialog" max-width="850px" scrollable>
      <v-card class="rounded-2xl voucher-detail-card" v-if="selectedVoucher">
        <!-- Dialog Header -->
        <div class="dialog-custom-header d-flex align-center justify-space-between pa-4 px-5">
          <div class="d-flex align-center gap-3">
            <div class="header-icon-circle bg-amber-soft">
              <v-icon color="#9a6b27" size="20">mdi-receipt-text-outline</v-icon>
            </div>
            <div>
              <div class="d-flex align-center gap-2">
                <h2 class="dialog-title mb-0">
                  Voucher #{{ (selectedVoucher.prefix ? selectedVoucher.prefix + '-' : '') + selectedVoucher.voucherNo }}
                </h2>
                <span :class="['type-pill', selectedVoucher.type === 'Issue' ? 'type-issue' : 'type-receipt']">
                  <span class="type-dot"></span>
                  {{ selectedVoucher.type }}
                </span>
              </div>
              <div class="dialog-subtitle">Date: {{ formatDate(selectedVoucher.date) }}</div>
            </div>
          </div>

          <div class="d-flex align-center gap-2">
            <v-btn
              outlined
              depressed
              small
              class="btn-action-outline mr-2"
              @click="printVoucherModal()"
            >
              <v-icon left x-small>mdi-printer</v-icon>
              Print
            </v-btn>
            <v-btn icon small @click="viewDialog = false">
              <v-icon color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider style="border-color: #f1f5f9;"></v-divider>

        <!-- Dialog Body -->
        <v-card-text class="pa-5" style="max-height: 600px;">
          <!-- Voucher Metadata Grid -->
          <div class="detail-info-grid mb-4">
            <div class="info-block">
              <span class="info-label">Supplier</span>
              <span class="info-value text-dark font-weight-bold">
                {{ selectedVoucher.supplierId ? selectedVoucher.supplierId.supplierName : (selectedVoucher.supplierName || '-') }}
              </span>
              <span class="info-sub" v-if="selectedVoucher.supplierId && selectedVoucher.supplierId.phone">
                Phone: {{ selectedVoucher.supplierId.phone }}
              </span>
            </div>

            <div class="info-block">
              <span class="info-label">Invoice Number</span>
              <span class="info-value font-weight-medium">
                {{ selectedVoucher.invoiceNumber || '-' }}
              </span>
              <span class="info-sub" v-if="selectedVoucher.invoiceDate">
                Inv Date: {{ formatDate(selectedVoucher.invoiceDate) }}
              </span>
            </div>

            <div class="info-block">
              <span class="info-label">Payment Mode</span>
              <span class="info-value">
                <v-icon x-small :color="selectedVoucher.paymentMode === 'Cash' ? '#16a34a' : '#2563eb'" class="mr-1">
                  {{ selectedVoucher.paymentMode === 'Cash' ? 'mdi-cash' : 'mdi-bank' }}
                </v-icon>
                {{ selectedVoucher.paymentMode || 'Standard' }}
              </span>
            </div>

            <div class="info-block" v-if="selectedVoucher.thankamRate">
              <span class="info-label">Thankam Rate</span>
              <span class="info-value font-weight-bold text-amber">
                ₹ {{ formatCurrency(selectedVoucher.thankamRate) }} /gm
              </span>
            </div>

            <div class="info-block" v-if="selectedVoucher.otherCharges">
              <span class="info-label">Other Charges</span>
              <span class="info-value font-weight-medium">
                ₹ {{ formatCurrency(selectedVoucher.otherCharges) }}
              </span>
            </div>
          </div>

          <!-- Items Table -->
          <div class="voucher-items-section mb-3">
            <h4 class="section-label mb-2">Itemized Breakdown</h4>
            <div class="table-responsive rounded-xl border-light">
              <table class="modal-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ITEM NAME / CODE</th>
                    <th>CATEGORY</th>
                    <th class="text-center">COUNT/SPLIT</th>
                    <th class="text-right">GROSS WT (gm)</th>
                    <th class="text-right">STONE WT (gm)</th>
                    <th class="text-right">NET WT (gm)</th>
                    <th class="text-right">TOUCH %</th>
                    <th class="text-right">MAKING CHARGE (₹)</th>
                    <th class="text-right">TOTAL (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in voucherItems" :key="idx">
                    <td>{{ idx + 1 }}</td>
                    <td>
                      <div class="font-weight-bold text-dark">{{ item.itemName || 'Gold Item' }}</div>
                      <div class="text-caption text-muted" v-if="item.itemCode">{{ item.itemCode }}</div>
                    </td>
                    <td>
                      <span class="cat-chip">{{ item.category || 'Gold' }}</span>
                    </td>
                    <td class="text-center">
                      <span v-if="item.countOrSplit === 'Count'">{{ item.quantity || 0 }} pcs</span>
                      <span v-else-if="item.countOrSplit === 'Split' && item.isBulk" class="text-muted">Bulk</span>
                      <span v-else-if="item.countOrSplit === 'Split'" class="text-muted">Split (by wt)</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td class="text-right">{{ (item.grossWeight || 0).toFixed(2) }}</td>
                    <td class="text-right">{{ (item.stoneWeight || 0).toFixed(2) }}</td>
                    <td class="text-right font-weight-bold">{{ (item.netWeight || 0).toFixed(2) }}</td>
                    <td class="text-right">{{ item.touch || '-' }}%</td>
                    <td class="text-right">{{ formatCurrency(item.makingCharge || 0) }}</td>
                    <td class="text-right font-weight-bold text-dark">
                      {{ formatCurrency(item.itemTotalAmount || 0) }}
                    </td>
                  </tr>
                  <tr v-if="!voucherItems || voucherItems.length === 0">
                    <td colspan="10" class="text-center py-4 text-muted">
                      No line items recorded for this voucher.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Totals Footer Summary Grid -->
          <div class="totals-summary-bar d-flex flex-wrap justify-end align-center gap-4 pa-3 rounded-xl">
            <div class="total-metric-col text-right">
              <span class="total-sub">Total Net Weight</span>
              <div class="total-num">{{ (getSelectedVoucherNetWeight()).toFixed(2) }} gm</div>
            </div>
            <div class="total-metric-col text-right pl-4" style="border-left: 1px solid #e2e8f0;">
              <span class="total-sub">Total Voucher Amount</span>
              <div class="total-num text-amber">₹ {{ formatCurrency(getVoucherTotalAmount(selectedVoucher)) }}</div>
            </div>
          </div>
        </v-card-text>

        <v-divider style="border-color: #f1f5f9;"></v-divider>

        <!-- Dialog Actions -->
        <div class="pa-4 px-5 text-right">
          <v-btn
            color="#9a6b27"
            dark
            depressed
            class="rounded-lg text-capitalize px-5 font-weight-bold"
            @click="viewDialog = false"
          >
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Modal Dialog -->
    <v-dialog v-model="deleteDialog" max-width="420px">
      <v-card class="rounded-2xl pa-5 text-center">
        <div class="delete-icon-circle mx-auto mb-3">
          <v-icon size="32" color="#ef4444">mdi-alert-outline</v-icon>
        </div>
        <h3 class="font-weight-bold text-dark mb-1">Delete Voucher?</h3>
        <p class="text-muted font-size-13 mb-4">
          Are you sure you want to delete voucher
          <strong class="text-dark">#{{ voucherToDelete ? ((voucherToDelete.prefix ? voucherToDelete.prefix + '-' : '') + voucherToDelete.voucherNo) : '' }}</strong>?
          This action will mark the voucher and its line items as deleted.
        </p>

        <div class="d-flex justify-center gap-3">
          <v-btn
            outlined
            depressed
            class="rounded-lg text-capitalize px-4 font-weight-medium btn-action-outline"
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="#ef4444"
            dark
            depressed
            class="rounded-lg text-capitalize px-5 font-weight-bold"
            @click="deleteVoucher()"
          >
            Yes, Delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Hidden Printable Area for Direct Voucher Printing -->
    <div id="printVoucherSection" style="display: none;">
      <div v-if="printData" style="font-family: Arial, sans-serif; padding: 24px; color: #1e293b;">
        <div style="text-align: center; border-bottom: 2px solid #9a6b27; padding-bottom: 12px; margin-bottom: 16px;">
          <h1 style="margin: 0; font-size: 24px; color: #9a6b27;">JEWELBIZ</h1>
          <p style="margin: 4px 0 0; font-size: 14px; color: #64748b;">Issue &amp; Receipt Voucher</p>
        </div>

        <table style="width: 100%; margin-bottom: 16px; font-size: 13px;">
          <tr>
            <td><strong>Voucher No:</strong> {{ (printData.prefix ? printData.prefix + '-' : '') + printData.voucherNo }}</td>
            <td style="text-align: right;"><strong>Date:</strong> {{ formatDate(printData.date) }}</td>
          </tr>
          <tr>
            <td><strong>Type:</strong> {{ printData.type }}</td>
            <td style="text-align: right;"><strong>Mode:</strong> {{ printData.paymentMode || '-' }}</td>
          </tr>
          <tr>
            <td><strong>Supplier:</strong> {{ printData.supplierId ? printData.supplierId.supplierName : (printData.supplierName || '-') }}</td>
            <td style="text-align: right;"><strong>Invoice No:</strong> {{ printData.invoiceNumber || '-' }}</td>
          </tr>
        </table>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 12px; table-layout: fixed;">
          <colgroup>
            <col style="width: 5%;">
            <col style="width: 21%;">
            <col style="width: 11%;">
            <col style="width: 13%;">
            <col style="width: 13%;">
            <col style="width: 13%;">
            <col style="width: 8%;">
            <col style="width: 16%;">
          </colgroup>
          <thead>
            <tr style="background-color: #f8fafc; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0;">
              <th style="padding: 8px 6px; text-align: left;">#</th>
              <th style="padding: 8px 6px; text-align: left;">Item</th>
              <th style="padding: 8px 6px; text-align: left;">Category</th>
              <th style="padding: 8px 6px; text-align: center;">Count/Split</th>
              <th style="padding: 8px 6px; text-align: right;">Gross Wt (gm)</th>
              <th style="padding: 8px 6px; text-align: right;">Net Wt (gm)</th>
              <th style="padding: 8px 6px; text-align: right;">Touch</th>
              <th style="padding: 8px 6px; text-align: right;">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(it, idx) in printItems" :key="idx" style="border-bottom: 1px solid #f1f5f9;">
              <td style="padding: 8px 6px;">{{ idx + 1 }}</td>
              <td style="padding: 8px 6px;">{{ it.itemName || 'Gold' }}</td>
              <td style="padding: 8px 6px;">{{ it.category || 'Gold' }}</td>
              <td style="padding: 8px 6px; text-align: center;">{{ it.countOrSplit === 'Count' ? (it.quantity || 0) + ' pcs' : (it.countOrSplit === 'Split' ? (it.isBulk ? 'Bulk' : 'Split (by wt)') : '-') }}</td>
              <td style="padding: 8px 6px; text-align: right;">{{ (it.grossWeight || 0).toFixed(2) }}</td>
              <td style="padding: 8px 6px; text-align: right;">{{ (it.netWeight || 0).toFixed(2) }}</td>
              <td style="padding: 8px 6px; text-align: right;">{{ it.touch || '-' }}%</td>
              <td style="padding: 8px 6px; text-align: right;">{{ formatCurrency(it.itemTotalAmount || 0) }}</td>
            </tr>
          </tbody>
        </table>

        <div style="text-align: right; margin-top: 16px; font-size: 14px;">
          <p style="margin: 4px 0;"><strong>Total Weight:</strong> {{ (printData.totalWeight || 0).toFixed(2) }} gm</p>
          <p style="margin: 4px 0; font-size: 16px; color: #9a6b27;"><strong>Total Amount:</strong> ₹ {{ formatCurrency(printData.totalAmount || 0) }}</p>
        </div>

        <div style="margin-top: 48px; display: flex; justify-content: space-between; font-size: 12px; color: #64748b;">
          <div>Authorized Signature</div>
          <div>Supplier Signature</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "IssueReceiptList",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      snackbarColor: "#1e293b",
      timeout: 2500,
      msg: null,

      // Pagination
      page: 1,
      itemsPerPage: 10,
      pageSizeOptions: [
        { text: "10 / page", value: 10 },
        { text: "20 / page", value: 20 },
        { text: "50 / page", value: 50 },
        { text: "100 / page", value: 100 },
      ],
      totalRecords: 0,
      Pagelength: 1,

      // Filters
      fromDate: "",
      toDate: "",
      menuFrom: false,
      menuTo: false,
      keyword: "",
      typeFilter: "",
      typeOptions: [
        { text: "All Types", value: "" },
        { text: "Issue", value: "Issue" },
        { text: "Receipt", value: "Receipt" },
      ],

      // Data Lists
      voucherList: [],
      backendSummary: [],

      // Sorting
      sortColumn: "date",
      sortAsc: false,
      autoRefreshTimer: null,

      // Detail Modal
      viewDialog: false,
      selectedVoucher: null,
      voucherItems: [],

      // Summary Modal
      summaryDialog: false,

      // Delete Modal
      deleteDialog: false,
      voucherToDelete: null,

      // Printing
      printData: null,
      printItems: [],
    };
  },
  watch: {
    page() {
      this.getList();
    },
    itemsPerPage() {
      this.page = 1;
      this.getList();
    },
    typeFilter() {
      this.page = 1;
      this.getList();
    },
    fromDate() {
      this.page = 1;
      this.getList();
    },
    toDate() {
      this.page = 1;
      this.getList();
    },
  },
  computed: {
    totalRecordsCount() {
      return this.totalRecords || (this.voucherList ? this.voucherList.length : 0);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRecordsCount / this.itemsPerPage));
    },
    paginationStart() {
      if (this.totalRecordsCount === 0) return 0;
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    paginationEnd() {
      return Math.min(this.page * this.itemsPerPage, this.totalRecordsCount);
    },
    visiblePages() {
      const total = this.totalPages;
      const cur = this.page;
      const pages = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) {
        pages.push(i);
      }
      return pages.length ? pages : [1];
    },
    displayedList() {
      if (!this.voucherList) return [];
      let list = [...this.voucherList];
      if (this.sortColumn) {
        list.sort((a, b) => {
          let valA = a[this.sortColumn];
          let valB = b[this.sortColumn];

          if (this.sortColumn === "supplierName") {
            valA = a.supplierId ? a.supplierId.supplierName : a.supplierName || "";
            valB = b.supplierId ? b.supplierId.supplierName : b.supplierName || "";
          }

          if (valA === undefined || valA === null) valA = "";
          if (valB === undefined || valB === null) valB = "";

          if (valA < valB) return this.sortAsc ? -1 : 1;
          if (valA > valB) return this.sortAsc ? 1 : -1;
          return 0;
        });
      }
      return list;
    },
    issueCount() {
      const item = this.backendSummary.find((s) => s._id === "Issue");
      if (item) return item.count;
      return this.voucherList.filter((v) => v.type === "Issue").length;
    },
    issueWeight() {
      const item = this.backendSummary.find((s) => s._id === "Issue");
      if (item) return item.totalWeight || 0;
      return this.voucherList
        .filter((v) => v.type === "Issue")
        .reduce((sum, v) => sum + (Number(v.totalWeight) || 0), 0);
    },
    receiptCount() {
      const item = this.backendSummary.find((s) => s._id === "Receipt");
      if (item) return item.count;
      return this.voucherList.filter((v) => v.type === "Receipt").length;
    },
    receiptWeight() {
      const item = this.backendSummary.find((s) => s._id === "Receipt");
      if (item) return item.totalWeight || 0;
      return this.voucherList
        .filter((v) => v.type === "Receipt")
        .reduce((sum, v) => sum + (Number(v.totalWeight) || 0), 0);
    },
    totalWeightSum() {
      if (this.backendSummary && this.backendSummary.length > 0) {
        return this.backendSummary.reduce((sum, s) => sum + (Number(s.totalWeight) || 0), 0);
      }
      return this.voucherList.reduce((sum, v) => sum + (Number(v.totalWeight) || 0), 0);
    },
    totalAmountSum() {
      if (this.backendSummary && this.backendSummary.length > 0) {
        return this.backendSummary.reduce((sum, s) => sum + (Number(s.totalAmount) || 0), 0);
      }
      return this.voucherList.reduce((sum, v) => sum + (Number(v.totalAmount) || 0), 0);
    },
  },
  mounted() {
    this.getList();
    this.autoRefreshTimer = setInterval(() => {
      this.getList(true);
    }, 30000);
  },
  beforeDestroy() {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
    }
  },
  methods: {
    sortBy(col) {
      if (this.sortColumn === col) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortColumn = col;
        this.sortAsc = true;
      }
    },
    getList(silent) {
      if (!silent) {
        this.appLoading = true;
      }
      axios({
        method: "get",
        url: "/issuereceipt/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          page: this.page,
          limit: this.itemsPerPage,
          type: this.typeFilter,
          keyword: this.keyword,
          from: this.fromDate,
          to: this.toDate,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.voucherList = response.data.data || [];
            this.totalRecords = response.data.count || this.voucherList.length;
            this.Pagelength = response.data.pages || Math.ceil(this.totalRecords / this.itemsPerPage);
            if (response.data.summary) {
              this.backendSummary = response.data.summary;
            }
          } else {
            this.msg = response.data.msg || "Failed to load vouchers";
            this.snackbarColor = "#ef4444";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          if (!silent) {
            this.ServerError = true;
          }
          console.log(err);
        });
    },
    resetFilters() {
      this.fromDate = "";
      this.toDate = "";
      this.typeFilter = "";
      this.keyword = "";
      this.page = 1;
      this.getList();
    },
    viewVoucher(item) {
      this.selectedVoucher = item;
      this.voucherItems = [];
      this.viewDialog = true;
      this.appLoading = true;

      axios({
        method: "get",
        url: "/issuereceipt/view",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { id: item._id },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.selectedVoucher = response.data.data || item;
            this.voucherItems = response.data.items || [];
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    confirmDelete(item) {
      this.voucherToDelete = item;
      this.deleteDialog = true;
    },
    deleteVoucher() {
      if (!this.voucherToDelete) return;
      this.appLoading = true;
      this.deleteDialog = false;

      axios({
        method: "post",
        url: "/issuereceipt/delete",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { id: this.voucherToDelete._id },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = "Voucher deleted successfully";
            this.snackbarColor = "#059669";
            this.showsnackbar = true;
            this.getList();
          } else {
            this.msg = response.data.msg || "Failed to delete voucher";
            this.snackbarColor = "#ef4444";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.msg = "Error deleting voucher";
          this.snackbarColor = "#ef4444";
          this.showsnackbar = true;
          console.log(err);
        });
    },
    printVoucherDirect(item) {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/issuereceipt/view",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { id: item._id },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.printData = response.data.data;
            this.printItems = response.data.items || [];
            this.$nextTick(() => {
              this.executePrint();
            });
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    printVoucherModal() {
      this.printData = this.selectedVoucher;
      this.printItems = this.voucherItems;
      this.$nextTick(() => {
        this.executePrint();
      });
    },
    executePrint() {
      const printContents = document.getElementById("printVoucherSection").innerHTML;
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Voucher - ${this.printData.voucherNo}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { padding: 8px; }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>
            ${printContents}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 300);
    },
    formatDate(d) {
      if (!d) return "-";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "-";
      const day = dt.getDate();
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = months[dt.getMonth()];
      const year = dt.getFullYear();
      return `${day} ${month} ${year}`;
    },
    formatCurrency(num) {
      if (num === null || num === undefined || isNaN(num)) return "0.00";
      return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    getVoucherTotalAmount(voucher) {
      if (!voucher) return 0;
      let total = parseFloat(voucher.totalAmount) || 0;
      const cash = voucher.cashPayment && voucher.cashPayment.amount ? parseFloat(voucher.cashPayment.amount) : 0;
      const payInsteadCash = voucher.payInsteadOfWeight && voucher.payInsteadOfWeight.amount ? parseFloat(voucher.payInsteadOfWeight.amount) : 0;
      const charges = parseFloat(voucher.otherCharges) || 0;
      if (voucher.settlementMode === "Cash" && cash > 0) {
        if (total === 0 || total === charges) {
          total = cash + charges;
        }
      } else if (voucher.settlementMode === "PayInsteadOfWeight" && payInsteadCash > 0) {
        if (total === 0 || total === charges) {
          total = payInsteadCash + charges;
        }
      }
      return total;
    },
    settlementModeMeta(mode) {
      if (mode === "Weight") {
        return { label: "Weight", icon: "mdi-gold", color: "#9a6b27" };
      }
      if (mode === "PayInsteadOfWeight") {
        return { label: "Pay Instead", icon: "mdi-swap-horizontal", color: "#2563eb" };
      }
      return { label: "Cash", icon: "mdi-cash", color: "#16a34a" };
    },
    getVoucherTotalWeight(voucher) {
      if (!voucher) return 0;
      let wt = parseFloat(voucher.totalWeight) || 0;
      if (wt === 0 && voucher.settlementMode === "Weight" && voucher.thankamWeight) {
        wt = parseFloat(voucher.thankamWeight) || 0;
      }
      return wt;
    },
    getSelectedVoucherNetWeight() {
      if (this.voucherItems && this.voucherItems.length > 0) {
        return this.voucherItems.reduce((acc, it) => acc + (parseFloat(it.netWeight) || 0), 0);
      }
      return this.getVoucherTotalWeight(this.selectedVoucher);
    },
  },
};
</script>

<style scoped>
.ir-list-page {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #1e293b;
  width: 100%;
}

/* Page Header & Breadcrumbs */
.breadcrumbs-row {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.breadcrumb-sep {
  color: #94a3b8;
  margin: 0 6px;
  font-weight: 300;
}

.breadcrumb-item {
  color: #64748b;
}

.breadcrumb-active {
  color: #9a6b27;
  font-weight: 600;
}

.page-main-title {
  font-size: 26px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.25;
  margin: 0;
  letter-spacing: -0.2px;
}

.page-subtitle {
  font-size: 13.5px;
  color: #64748b;
  font-weight: 400;
  margin-top: 3px;
}

/* Header Buttons */
.btn-new-voucher {
  height: 38px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  padding: 0 16px !important;
  letter-spacing: 0.1px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-new-voucher:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

.btn-action-outline {
  height: 38px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  color: #334151 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 14px !important;
  letter-spacing: 0.1px !important;
  transition: all 0.2s ease !important;
}

.btn-action-outline .btn-icon {
  color: #64748b !important;
  transition: color 0.2s ease;
}

.btn-action-outline:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
  background-color: #fdfaf6 !important;
  transform: translateY(-1px);
}

.btn-action-outline:hover .btn-icon {
  color: #9a6b27 !important;
}

.btn-icon-only {
  min-width: 38px !important;
  width: 38px !important;
  padding: 0 !important;
}

/* Filter Bar */
.filter-card-bar {
  background-color: transparent;
}

.filter-row {
  gap: 8px;
}

.date-item {
  width: 135px;
  flex-shrink: 0;
}

.type-item {
  width: 125px;
  flex-shrink: 0;
}

.search-item {
  min-width: 200px;
}

.filter-input-field >>> .v-input__control {
  min-height: 38px !important;
}

.filter-input-field >>> .v-input__slot {
  min-height: 38px !important;
  height: 38px !important;
  font-size: 13px !important;
  padding: 0 12px !important;
  border-radius: 8px !important;
  border-color: #e2e8f0 !important;
  background-color: #ffffff !important;
}

.filter-input-field >>> fieldset {
  border-color: #e2e8f0 !important;
}

.filter-input-field:hover >>> fieldset {
  border-color: #cbd5e1 !important;
}

.filter-input-field.v-input--is-focused >>> fieldset {
  border-color: #9a6b27 !important;
}

.filter-input-field >>> .v-text-field__details {
  display: none !important;
}

.filter-input-field >>> .v-input__append-inner,
.filter-input-field >>> .v-input__prepend-inner {
  margin-top: 7px !important;
}

.filter-input-field >>> .v-icon {
  font-size: 18px !important;
  color: #94a3b8 !important;
}

.type-select >>> .v-select__selection {
  font-size: 13px !important;
  color: #334151 !important;
  font-weight: 500 !important;
}

.btn-search-amber {
  height: 38px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  padding: 0 18px !important;
  letter-spacing: 0.1px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-search-amber:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

.btn-reset-outline {
  height: 38px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  color: #334151 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  letter-spacing: 0.1px !important;
  transition: all 0.2s ease !important;
}

.btn-reset-outline:hover {
  border-color: #cbd5e1 !important;
  background-color: #f8fafc !important;
  color: #0f172a !important;
}

/* Main Table Card */
.table-card {
  background-color: #ffffff;
  border: 1px solid #e8e4dc;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02) !important;
  overflow: hidden;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.custom-table thead {
  background-color: #ffffff;
}

.custom-table th {
  padding: 12px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid #f1f5f9;
  user-select: none;
  cursor: pointer;
  line-height: 1.3;
}

.custom-table th:hover {
  color: #1e293b;
}

.sort-icon {
  color: #94a3b8 !important;
  margin-left: 2px;
  vertical-align: middle;
}

.custom-table td {
  padding: 12px 10px;
  font-size: 13px;
  color: #1e293b;
  border-bottom: 1px solid #f8fafc;
  white-space: nowrap;
  vertical-align: middle;
}

.table-row {
  transition: background-color 0.12s ease;
}

.table-row:hover {
  background-color: #fafaf9;
}

/* Column Sizing */
.col-sno { width: 42px; min-width: 38px; }
.col-voucher { width: 105px; min-width: 95px; }
.col-date { width: 115px; min-width: 105px; }
.col-type { width: 95px; min-width: 90px; }
.col-supplier { min-width: 150px; }
.col-inv-no { width: 100px; min-width: 85px; }
.col-mode { width: 95px; min-width: 85px; }
.col-weight { width: 115px; min-width: 105px; }
.col-amount { width: 125px; min-width: 115px; }
.col-actions { width: 85px; min-width: 80px; }

/* Cell Typography & Badges */
.cell-sno {
  color: #1e293b;
  font-weight: 600;
  font-size: 13px;
}

.voucher-pill {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  background-color: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  display: inline-block;
}

.date-text {
  font-weight: 500;
  color: #334151;
  font-size: 13px;
}

.supplier-text {
  text-transform: capitalize;
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
}

.inv-text {
  color: #475569;
  font-weight: 500;
  font-size: 13px;
}

.weight-val {
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
  font-feature-settings: "tnum";
}

.amount-val {
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
  font-feature-settings: "tnum";
}

/* Status Type Pills */
.type-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  gap: 5px;
}

.type-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.type-issue {
  background-color: #eff6ff;
  color: #2563eb;
}

.dot-issue {
  background-color: #2563eb;
}

.type-receipt {
  background-color: #dcfce7;
  color: #16a34a;
}

.dot-receipt {
  background-color: #16a34a;
}

.mode-pill {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background-color: #f8fafc;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
}

/* Table Action Buttons */
.table-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}

.table-action-btn:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

/* Empty State */
.empty-state-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

/* Table Footer & Pagination */
.table-footer {
  background-color: #ffffff;
  border-top: 1px solid #f1f5f9;
}

.footer-records-text {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.pagination-btn {
  height: 32px;
  min-width: 32px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  color: #334151;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.pagination-btn:hover:not(:disabled) {
  border-color: #cbd5e1;
  background-color: #f8fafc;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-active {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important; background-color: #9a6b27 !important;
  border-color: #9a6b27 !important;
  color: #ffffff !important;
}

.page-size-select >>> .v-input__control {
  min-height: 32px !important;
}

.page-size-select >>> .v-input__slot {
  min-height: 32px !important;
  height: 32px !important;
  font-size: 12px !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
}

.page-size-select >>> .v-select__selection {
  font-size: 12px !important;
}

.page-size-select >>> .v-input__append-inner {
  margin-top: 4px !important;
}

/* Summary Modal Metric Cards */
.summary-metric-card {
  background-color: #f8fafc;
  border-radius: 12px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
}

.metric-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 2px;
}

.metric-value {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.metric-sub {
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 2px;
}

.status-issue-bg {
  background-color: #eff6ff !important;
  border-color: #bfdbfe !important;
}

.status-receipt-bg {
  background-color: #ecfdf5 !important;
  border-color: #a7f3d0 !important;
}

.total-amount-bg {
  background-color: #fdf6ec !important;
  border-color: #fed7aa !important;
}

.text-blue { color: #2563eb !important; }
.text-green { color: #16a34a !important; }
.text-amber { color: #9a6b27 !important; }

/* Detail Modal Styles */
.voucher-detail-card {
  background-color: #ffffff;
}

.dialog-custom-header {
  background-color: #fafaf9;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

.dialog-subtitle {
  font-size: 12.5px;
  color: #64748b;
}

.header-icon-circle {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-amber-soft {
  background-color: #fdf6ec;
}

.detail-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  background-color: #f8fafc;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
}

.info-block {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
}

.info-value {
  font-size: 13.5px;
  color: #1e293b;
}

.info-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}

.section-label {
  font-size: 13px;
  font-weight: 700;
  color: #334151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.border-light {
  border: 1px solid #f1f5f9;
}

.modal-items-table {
  width: 100%;
  border-collapse: collapse;
}

.modal-items-table th {
  background-color: #f8fafc;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0;
}

.modal-items-table td {
  padding: 10px 12px;
  font-size: 12.5px;
  color: #1e293b;
  border-bottom: 1px solid #f8fafc;
}

.cat-chip {
  font-size: 11px;
  font-weight: 600;
  background-color: #f1f5f9;
  color: #475569;
  padding: 2px 6px;
  border-radius: 4px;
}

.totals-summary-bar {
  background-color: #fdfaf6;
  border: 1px solid #faeade;
}

.total-sub {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  display: block;
}

.total-num {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.delete-icon-circle {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background-color: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }

@media (max-width: 768px) {
  .date-item, .type-item {
    width: 100%;
  }
}
</style>
